import { useState, useEffect, useContext, useMemo, useRef } from 'react';
import { Box, Grid, Typography, Dialog, IconButton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { IoClose } from 'react-icons/io5';
import { LuClipboardList, LuLogIn, LuLogOut, LuCalendarDays, LuHome } from 'react-icons/lu';
import { ToastMessageContext } from '@context/toast-message-cotnext';
import { usePutManualReservation } from '../../../../services/manualReservation/putManualReservation';
import { useGetManualReservation } from '../../../../services/manualReservation/getManualReservation';
import { useReservationDetail, useAvailableProperties } from '../../services/reservations.queries';
import {
  useEarlyCheckinValidate,
  useEarlyCheckinApply,
  useLateCheckoutValidate,
  useLateCheckoutApply,
  useExtendStayValidate,
  useExtendStayApply,
  useMoveToProperty,
} from '../../services/reservations.mutations';
import { RESERVATION_LIST_URL, RESERVATION_DETAIL_URL, TASK_MANAGEMENT_URL, TASK_MANAGEMENT_STATS_URL } from '../../constants';
import { useQueryClient } from '@tanstack/react-query';
import type { IEarlyCheckinValidateResult, ILateCheckoutValidateResult, IExtendStayValidateResult } from '../../types';
import BookingDataTab from './BookingDataTab';
import EarlyCheckinTab from './EarlyCheckinTab';
import LateCheckoutTab from './LateCheckoutTab';
import ExtendStayTab from './ExtendStayTab';
import ExtendWithMoveTab from './ExtendWithMoveTab';

type InnerTab = 'bookingData' | 'earlyCheckin' | 'lateCheckout' | 'extendStay' | 'extendWithMove';

interface ReservationModalProps {
  open: boolean;
  onClose: () => void;
  reservationId: number | undefined;
  tabType?: 'upcoming' | 'checkin' | 'checkout';
  listItemCheckIn?: string;
  listItemCheckOut?: string;
}

/**
 * Extracts "HH:mm" from various time formats:
 * - ISO date-time: "2026-03-24T00:00:00+03:00" or "2026-03-24T14:30:00"
 * - Arabic 12h: "12:00 ص", "02:30 م"
 * - Plain 24h: "14:30"
 * Returns empty string if parsing fails.
 */
const parseTimeTo24h = (displayTime: string | undefined | null): string => {
  if (!displayTime) return '';
  const trimmed = displayTime.trim();

  // ISO date-time format: contains "T" separator (e.g. "2026-03-24T00:00:00+03:00")
  const isoMatch = trimmed.match(/T(\d{2}):(\d{2})/);
  if (isoMatch) return `${isoMatch[1]}:${isoMatch[2]}`;

  // Arabic 12h format: HH:MM followed by ص (AM) or م (PM)
  const arabicMatch = trimmed.match(/^(\d{1,2}):(\d{2})\s*(ص|م)$/i);
  if (arabicMatch) {
    let hours = parseInt(arabicMatch[1], 10);
    const minutes = arabicMatch[2];
    const period = arabicMatch[3];
    if (period === 'ص') {
      if (hours === 12) hours = 0;
    } else {
      if (hours !== 12) hours += 12;
    }
    return `${String(hours).padStart(2, '0')}:${minutes}`;
  }

  // Space-separated datetime: "2026-03-29 15:00" or "2026-03-29 15:00:00"
  const spaceMatch = trimmed.match(/\d{4}-\d{2}-\d{2}\s+(\d{2}):(\d{2})/);
  if (spaceMatch) return `${spaceMatch[1]}:${spaceMatch[2]}`;

  // Plain 24h format: "14:30" or "14:30:00"
  const match24 = trimmed.match(/^(\d{1,2}):(\d{2})/);
  if (match24) return `${String(parseInt(match24[1], 10)).padStart(2, '0')}:${match24[2]}`;

  return '';
};

const ALL_SIDEBAR_TABS: { key: InnerTab; tKey: string; icon: React.ReactNode }[] = [
  {
    key: 'bookingData',
    tKey: 'TaskManagemnt.tabBookingData',
    icon: <LuClipboardList size={18} />,
  },
  {
    key: 'earlyCheckin',
    tKey: 'TaskManagemnt.tabEarlyCheckin',
    icon: <LuLogIn size={18} />,
  },
  {
    key: 'lateCheckout',
    tKey: 'TaskManagemnt.tabLateCheckout',
    icon: <LuLogOut size={18} />,
  },
  {
    key: 'extendStay',
    tKey: 'TaskManagemnt.tabExtendStay',
    icon: <LuCalendarDays size={18} />,
  },
  {
    key: 'extendWithMove',
    tKey: 'TaskManagemnt.tabExtendWithMove',
    icon: <LuHome size={18} />,
  },
];

const ReservationModal = ({ open, onClose, reservationId, tabType = 'upcoming', listItemCheckIn, listItemCheckOut }: ReservationModalProps) => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ar';
  const { showSuccess, showError } = useContext(ToastMessageContext);
  const queryClient = useQueryClient();

  const [activeInnerTab, setActiveInnerTab] = useState<InnerTab>('bookingData');
  const [isSaving, setIsSaving] = useState(false);

  // Form state for BookingDataTab
  const [formData, setFormData] = useState<{
    checkIn: string;
    checkOut: string;
    checkInTime: string;
    checkOutTime: string;
    notes: string;
  }>({
    checkIn: '',
    checkOut: '',
    checkInTime: '',
    checkOutTime: '',
    notes: '',
  });

  // Early Check-in state
  const [earlyCheckinTime, setEarlyCheckinTime] = useState('');
  const [earlyCheckinValidationResult, setEarlyCheckinValidationResult] = useState<IEarlyCheckinValidateResult | null>(null);
  const earlyCheckinUserSelected = useRef(false);

  // Late Checkout state
  const [lateCheckoutTime, setLateCheckoutTime] = useState('');
  const [lateCheckoutValidationResult, setLateCheckoutValidationResult] = useState<ILateCheckoutValidateResult | null>(null);

  // Extend Stay state
  const [newCheckoutDate, setNewCheckoutDate] = useState('');
  const [extendValidationResult, setExtendValidationResult] = useState<IExtendStayValidateResult | null>(null);
  const [isDirectPaymentAcceptedExtend, setIsDirectPaymentAcceptedExtend] = useState(false);
  const [guestEmailExtend, setGuestEmailExtend] = useState('');
  const [isExtendStayValid, setIsExtendStayValid] = useState(false);

  // Extend With Move state
  const [extensionDate, setExtensionDate] = useState('');
  const [selectedPropertyId, setSelectedPropertyId] = useState<number | null>(null);
  const [isDirectPaymentAcceptedMove, setIsDirectPaymentAcceptedMove] = useState(false);
  const [guestEmailMove, setGuestEmailMove] = useState('');
  const [isExtendWithMoveValid, setIsExtendWithMoveValid] = useState(false);

  // Determine which tabs are visible and enabled based on tabType
  const sidebarTabs = useMemo(() => {
    if (tabType === 'checkout') {
      return ALL_SIDEBAR_TABS.filter((tab) => tab.key !== 'earlyCheckin');
    }
    return ALL_SIDEBAR_TABS.filter((tab) => tab.key !== 'lateCheckout');
  }, [tabType]);

  const enabledTabs = useMemo<Set<InnerTab>>(() => {
    if (tabType === 'checkin') {
      return new Set(['bookingData', 'earlyCheckin', 'extendStay', 'extendWithMove']);
    }
    if (tabType === 'checkout') {
      return new Set(['bookingData', 'lateCheckout', 'extendStay', 'extendWithMove']);
    }
    return new Set(['bookingData']);
  }, [tabType]);

  // Fetch reservation detail
  const { data: detailResponse, isLoading: detailLoading } = useReservationDetail(
    open ? reservationId : undefined
  );
  const detail = detailResponse?.result ?? null;

  // Fetch ManualReservation detail to get phone number in the format the PUT API expects
  const { data: manualReservationResponse } = useGetManualReservation(
    { id: String(reservationId ?? '') },
    { enabled: open && !!reservationId }
  );
  const manualReservationPhone = manualReservationResponse?.result?.guestPhoneNumber;

  // Sync form state when detail or manualReservation loads
  useEffect(() => {
    if (detail) {
      // Use ISO dates from list item if available, otherwise fall back to display strings
      const checkInDate = listItemCheckIn
        ? listItemCheckIn.substring(0, 10)
        : detail.checkInDateDisplay || '';
      const checkOutDate = listItemCheckOut
        ? listItemCheckOut.substring(0, 10)
        : detail.checkOutDateDisplay || '';

      // ManualReservation API has correct times ("2026-03-29 15:00");
      // TaskManagement API returns wrong midnight timestamps for checkInTimeDisplay.
      const mrResult = manualReservationResponse?.result;
      const checkInTime = mrResult
        ? parseTimeTo24h(mrResult.checkIn)
        : parseTimeTo24h(detail.checkInTimeDisplay);
      const checkOutTime = mrResult
        ? parseTimeTo24h(mrResult.checkOut)
        : parseTimeTo24h(detail.checkOutTimeDisplay);

      setFormData({
        checkIn: checkInDate,
        checkOut: checkOutDate,
        checkInTime,
        checkOutTime,
        notes: detail.notes || '',
      });
      setNewCheckoutDate(checkOutDate);
    }
  }, [detail, listItemCheckIn, listItemCheckOut, manualReservationResponse]);

  // Reset all state when modal opens
  useEffect(() => {
    if (open) {
      setActiveInnerTab('bookingData');
      setEarlyCheckinTime('');
      earlyCheckinUserSelected.current = false;
      setEarlyCheckinValidationResult(null);
      setLateCheckoutTime('');
      setLateCheckoutValidationResult(null);
      setNewCheckoutDate('');
      setExtendValidationResult(null);
      setIsDirectPaymentAcceptedExtend(false);
      setGuestEmailExtend('');
      setIsExtendStayValid(false);
      setExtensionDate('');
      setSelectedPropertyId(null);
      setIsDirectPaymentAcceptedMove(false);
      setGuestEmailMove('');
      setIsExtendWithMoveValid(false);
    }
  }, [open]);

  // Pre-populate earlyCheckinTime with current check-in time once formData loads
  // (set directly without triggering validate API — stops once user manually picks a time)
  useEffect(() => {
    if (open && formData.checkInTime && !earlyCheckinUserSelected.current) {
      setEarlyCheckinTime(formData.checkInTime);
    }
  }, [open, formData.checkInTime]);

  // --- Mutations ---
  const putMutation = usePutManualReservation();
  const earlyCheckinValidate = useEarlyCheckinValidate();
  const earlyCheckinApply = useEarlyCheckinApply();
  const lateCheckoutValidate = useLateCheckoutValidate();
  const lateCheckoutApply = useLateCheckoutApply();
  const extendStayValidate = useExtendStayValidate();
  const extendStayApply = useExtendStayApply();
  const moveToProperty = useMoveToProperty();

  // Available properties for Extend With Move
  const { data: availablePropertiesResponse, isLoading: propertiesLoading } = useAvailableProperties(
    extensionDate && reservationId
      ? { reservationId, extensionCheckoutDate: extensionDate }
      : undefined
  );
  const availableProperties = availablePropertiesResponse?.result ?? [];

  // Build full ISO date-time from the reservation's check-in date + selected HH:mm.
  // When check-in is at midnight (00:00), early check-in must be on the previous calendar
  // day, so we subtract 1 day from the date before building the ISO string.
  const buildEarlyCheckinIso = (time: string): string => {
    const rawDate = formData.checkIn || new Date().toISOString().substring(0, 10);
    const checkinHour = formData.checkInTime
      ? parseInt(formData.checkInTime.split(':')[0], 10)
      : NaN;
    if (!isNaN(checkinHour) && checkinHour === 0) {
      const d = new Date(`${rawDate}T00:00:00`);
      d.setDate(d.getDate() - 1);
      const prevDay = d.toISOString().substring(0, 10);
      return `${prevDay}T${time}:00`;
    }
    return `${rawDate}T${time}:00`;
  };

  // Build full ISO date-time from the reservation's check-out date + selected HH:mm
  const buildLateCheckoutIso = (time: string): string => {
    const datePart = formData.checkOut || new Date().toISOString().substring(0, 10);
    return `${datePart}T${time}:00`;
  };

  // --- Early Check-in: validate on time change ---
  const handleEarlyCheckinTimeChange = async (time: string) => {
    earlyCheckinUserSelected.current = true;
    setEarlyCheckinTime(time);
    setEarlyCheckinValidationResult(null);
    if (!time || !reservationId) return;
    try {
      const response = await earlyCheckinValidate.mutateAsync({
        reservationId,
        newCheckinTime: buildEarlyCheckinIso(time),
      });
      if (response?.result) {
        setEarlyCheckinValidationResult(response.result);
      }
    } catch (err: any) {
      // 422 responses are thrown as errors by axios — extract the result from response data
      const responseData = err?.response?.data;
      if (responseData?.result) {
        setEarlyCheckinValidationResult(responseData.result);
      } else {
        // Fallback: build a minimal result from errorMessages
        const msg = responseData?.errorMessages?.[0];
        const messageAr = typeof msg === 'object' ? msg?.messageAr : '';
        const messageEn = typeof msg === 'object' ? msg?.messageEn : (msg || '');
        setEarlyCheckinValidationResult({
          isValid: false,
          currentCheckinTime: '',
          proposedCheckinTime: '',
          conflictsWithPreviousBooking: false,
          impactsPreviousGuest: false,
          previousBookingCheckOut: null,
          minutesAfterPreviousCheckOut: null,
          messageEn: messageEn || t('TaskManagemnt.earlyCheckinUnavailable'),
          messageAr: messageAr || t('TaskManagemnt.earlyCheckinUnavailable'),
        });
      }
    }
  };

  // --- Late Checkout: validate on time change ---
  const handleLateCheckoutTimeChange = async (time: string) => {
    setLateCheckoutTime(time);
    setLateCheckoutValidationResult(null);
    if (!time || !reservationId) return;
    try {
      const response = await lateCheckoutValidate.mutateAsync({
        reservationId,
        newCheckoutTime: buildLateCheckoutIso(time),
      });
      if (response?.result) {
        setLateCheckoutValidationResult(response.result);
      }
    } catch (err: any) {
      const responseData = err?.response?.data;
      if (responseData?.result) {
        setLateCheckoutValidationResult(responseData.result);
      } else {
        const msg = responseData?.errorMessages?.[0];
        const messageAr = typeof msg === 'object' ? msg?.messageAr : '';
        const messageEn = typeof msg === 'object' ? msg?.messageEn : (msg || '');
        setLateCheckoutValidationResult({
          isValid: false,
          currentCheckoutTime: '',
          proposedCheckoutTime: time,
          conflictsWithNextBooking: false,
          impactsNextGuest: false,
          nextBookingCheckIn: null,
          minutesUntilNextCheckIn: null,
          messageEn: messageEn || t('TaskManagemnt.lateCheckoutUnavailable'),
          messageAr: messageAr || t('TaskManagemnt.lateCheckoutUnavailable'),
        });
      }
    }
  };

  // --- Extend Stay: validate on date change ---
  const handleExtendDateChange = async (date: string) => {
    setNewCheckoutDate(date);
    setExtendValidationResult(null);
    if (!date || !reservationId) return;
    try {
      const result = await extendStayValidate.mutateAsync({
        reservationId,
        newCheckoutDate: date,
      });
      if (result?.result) {
        setExtendValidationResult(result.result);
      }
    } catch (err: any) {
      // 422 responses are thrown by axios — extract result from response data
      const responseData = err?.response?.data;
      if (responseData?.result) {
        setExtendValidationResult(responseData.result);
      } else {
        const msg = responseData?.errorMessages?.[0];
        const messageAr = typeof msg === 'object' ? msg?.messageAr : '';
        const messageEn = typeof msg === 'object' ? msg?.messageEn : (msg || '');
        setExtendValidationResult({
          isValid: false,
          currentCheckoutDate: '',
          proposedCheckoutDate: date,
          conflictsWithNextBooking: false,
          conflictsWithBlockedDates: false,
          nextBookingCheckIn: null,
          messageEn: messageEn || t('TaskManagemnt.extendError'),
          messageAr: messageAr || t('TaskManagemnt.extendError'),
        });
      }
    }
  };

  // --- Extend With Move: sync extension date from extend stay ---
  const handleSwitchToExtendWithMove = () => {
    setActiveInnerTab('extendWithMove');
    if (newCheckoutDate) {
      setExtensionDate(newCheckoutDate);
    }
  };

  // --- Save logic per tab ---
  const handleSave = async () => {
    if (!detail || !reservationId) return;
    setIsSaving(true);
    try {
      if (activeInnerTab === 'bookingData') {
        await handleSaveBookingData();
      } else if (activeInnerTab === 'earlyCheckin') {
        await handleSaveEarlyCheckin();
      } else if (activeInnerTab === 'lateCheckout') {
        await handleSaveLateCheckout();
      } else if (activeInnerTab === 'extendStay') {
        await handleSaveExtendStay();
      } else if (activeInnerTab === 'extendWithMove') {
        await handleSaveExtendWithMove();
      }
    } catch {
      showError(t('TaskManagemnt.saveError'));
    } finally {
      setIsSaving(false);
    }
  };

  const extractErrorMessage = (errorMessages: any[] | undefined): string | undefined => {
    if (!errorMessages || errorMessages.length === 0) return undefined;
    const first = errorMessages[0];
    if (typeof first === 'string') return first;
    if (typeof first === 'object' && first !== null) {
      return isRtl ? first.messageAr : first.messageEn;
    }
    return undefined;
  };

  const handleSaveBookingData = async () => {
    if (!detail || !reservationId) return;
    const nameParts = (detail.guestName || '').split(' ');
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';

    const response: any = await putMutation.mutateAsync({
      id: String(reservationId),
      data: {
        id: String(reservationId),
        firstName,
        lastName,
        checkIn: formData.checkIn,
        checkOut: formData.checkOut,
        propertyId: String(detail.propertyId),
        propertyName: detail.propertyName || '',
        checkInTime: formData.checkInTime,
        checkOutTime: formData.checkOutTime,
        notes: formData.notes || '',
        guestPhoneNumber: manualReservationPhone ?? (detail.guestPhoneNumber || '').replace(/\s/g, ''),
        totalPrice: detail.totalPrice
      },
    });

    if (response?.data?.isSuccess || response?.status === 200) {
      showSuccess(t('TaskManagemnt.saveSuccess'));
      invalidateAll();
      onClose();
    } else {
      const errMsg = extractErrorMessage(response?.data?.errorMessages);
      showError(errMsg || t('TaskManagemnt.saveError'));
    }
  };

  const handleSaveLateCheckout = async () => {
    if (!reservationId || !lateCheckoutTime) return;
    const result = await lateCheckoutApply.mutateAsync({
      reservationId,
      newCheckoutTime: buildLateCheckoutIso(lateCheckoutTime),
    });
    if (result?.isSuccess) {
      showSuccess(t('TaskManagemnt.lateCheckoutApplied'));
      invalidateAll();
      onClose();
    } else {
      const errMsg = extractErrorMessage(result?.errorMessages);
      showError(errMsg || t('TaskManagemnt.saveError'));
    }
  };

  const handleSaveEarlyCheckin = async () => {
    if (!reservationId || !earlyCheckinTime) return;
    const result = await earlyCheckinApply.mutateAsync({
      reservationId,
      newCheckinTime: buildEarlyCheckinIso(earlyCheckinTime),
    });
    if (result?.isSuccess) {
      showSuccess(t('TaskManagemnt.earlyCheckinApplied'));
      invalidateAll();
      onClose();
    } else {
      const errMsg = extractErrorMessage(result?.errorMessages);
      showError(errMsg || t('TaskManagemnt.saveError'));
    }
  };

  const handleSaveExtendStay = async () => {
    if (!reservationId || !newCheckoutDate) return;
    const result = await extendStayApply.mutateAsync({
      reservationId,
      newCheckoutDate,
      isDirectPaymentAccepted: isDirectPaymentAcceptedExtend,
      guestEmail: guestEmailExtend,
    });
    if (result?.isSuccess) {
      showSuccess(t('TaskManagemnt.saveSuccess'));
      onClose();
    } else {
      const errMsg = extractErrorMessage(result?.errorMessages);
      showError(errMsg || t('TaskManagemnt.saveError'));
    }
  };

  const handleSaveExtendWithMove = async () => {
    if (!reservationId || !selectedPropertyId || !extensionDate || !detail) return;
    const result = await moveToProperty.mutateAsync({
      originalReservationId: reservationId,
      targetPropertyId: selectedPropertyId,
      extensionCheckInDate: detail.checkOutDateDisplay,
      extensionCheckOutDate: extensionDate,
      isDirectPaymentAccepted: isDirectPaymentAcceptedMove,
      guestEmail: guestEmailMove,
    });
    if (result?.isSuccess) {
      showSuccess(t('TaskManagemnt.saveSuccess'));
      onClose();
    } else {
      const errMsg = extractErrorMessage(result?.errorMessages);
      showError(errMsg || t('TaskManagemnt.saveError'));
    }
  };

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: [RESERVATION_LIST_URL] });
    queryClient.invalidateQueries({ queryKey: [RESERVATION_DETAIL_URL] });
    queryClient.invalidateQueries({ queryKey: [TASK_MANAGEMENT_URL] });
    queryClient.invalidateQueries({ queryKey: [TASK_MANAGEMENT_STATS_URL] });
  };

  // Determine if save button should be disabled
  const isSaveDisabled = (() => {
    if (isSaving) return true;
    if (activeInnerTab === 'earlyCheckin' && (!earlyCheckinTime || earlyCheckinValidationResult?.isValid !== true || earlyCheckinValidate.isPending)) return true;
    if (activeInnerTab === 'lateCheckout' && (!lateCheckoutTime || lateCheckoutValidationResult?.isValid !== true || lateCheckoutValidate.isPending)) return true;
    if (activeInnerTab === 'extendStay' && (!newCheckoutDate || (extendValidationResult && !extendValidationResult.isValid) || !isExtendStayValid)) return true;
    if (activeInnerTab === 'extendWithMove' && (!selectedPropertyId || !isExtendWithMoveValid)) return true;
    return false;
  })();

  // Modal title based on tabType
  const modalTitle = tabType === 'checkin'
    ? t('TaskManagemnt.modalCheckinTitle')
    : tabType === 'checkout'
      ? t('TaskManagemnt.modalCheckoutTitle')
      : t('TaskManagemnt.modalUpcomingTitle');

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: '900px',
          maxWidth: '95vw',
          maxHeight: '90vh',
          borderRadius: '24px',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* Static Header */}
      <Box sx={{ flexShrink: 0, pt: '40px', px: '24px', pb: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <Typography
            sx={{
              fontFamily: 'Tajawal, sans-serif',
              fontWeight: 700,
              fontSize: '24px',
              color: '#261B6A',
              letterSpacing: '-0.6px',
              lineHeight: '34px',
            }}
          >
            {modalTitle}
          </Typography>
          <Typography
            sx={{
              fontFamily: 'Tajawal, sans-serif',
              fontWeight: 400,
              fontSize: '16px',
              color: '#8E98A1',
              letterSpacing: '-0.4px',
              lineHeight: '45px',
            }}
          >
            {t('TaskManagemnt.modalSubtitle')}
          </Typography>
        </div>
        <IconButton onClick={onClose} sx={{ mt: '-8px', mr: '-8px' }}>
          <IoClose size={24} color="#435363" />
        </IconButton>

      </Box>

      {/* Scrollable Content */}
      <Box sx={{ flex: 1, overflowY: 'auto', px: '24px', py: '8px' }}>
        <Grid container spacing={3}>
          {/* Sidebar tabs */}
          <Grid item xs={12} md={3}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {sidebarTabs.map((tab) => {
                const isActive = activeInnerTab === tab.key;
                const isEnabled = enabledTabs.has(tab.key);
                return (
                  <Box
                    key={tab.key}
                    onClick={() => isEnabled && setActiveInnerTab(tab.key)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      px: '12px',
                      py: '8px',
                      borderRadius: '8px',
                      cursor: isEnabled ? 'pointer' : 'default',
                      opacity: isEnabled ? 1 : 0.5,
                      backgroundColor: isActive ? '#E1DCFF' : '#FFFFFF',
                      border: isActive ? '1px solid #5B3FFF' : 'none',
                      boxShadow: '0px 0px 1px 0px rgba(12,26,75,0.1), 0px 4px 20px 0px rgba(50,50,71,0.08)',
                      transition: 'all 0.15s ease',
                      '&:hover': isEnabled && !isActive ? { backgroundColor: '#F9F8FF' } : {},
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: 'Tajawal, sans-serif',
                        fontWeight: 500,
                        fontSize: '12px',
                        color: isActive ? '#5B3FFF' : '#8E98A1',
                        width: '131px',
                      }}
                    >
                      {t(tab.tKey)}
                    </Typography>
                    <Box sx={{ color: isActive ? '#5B3FFF' : '#8E98A1', display: 'flex' }}>{tab.icon}</Box>
                  </Box>
                );
              })}
            </Box>
          </Grid>

          {/* Active tab content */}
          <Grid item xs={12} md={9}>
            <Box
              sx={{
                backgroundColor: '#FFFFFF',
                borderRadius: '8px',
                boxShadow: '0px 0px 1px 0px rgba(12,26,75,0.1), 0px 4px 20px 0px rgba(50,50,71,0.08)',
                p: '24px',
                minHeight: '400px',
              }}
            >
              {detailLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <div
                    className="animate-spin rounded-full h-8 w-8"
                    style={{ border: '2px solid #E8E8EA', borderTopColor: '#5B3FFF' }}
                  />
                </Box>
              ) : activeInnerTab === 'bookingData' && detail ? (
                <BookingDataTab
                  detail={detail}
                  formData={formData}
                  onFormChange={setFormData}
                  isRtl={isRtl}
                />
              ) : activeInnerTab === 'earlyCheckin' ? (
                <EarlyCheckinTab
                  selectedTime={earlyCheckinTime}
                  onTimeChange={handleEarlyCheckinTimeChange}
                  currentCheckinTime={formData.checkInTime}
                  isValidating={earlyCheckinValidate.isPending}
                  validationResult={earlyCheckinValidationResult}
                  isRtl={isRtl}
                />
              ) : activeInnerTab === 'lateCheckout' ? (
                <LateCheckoutTab
                  selectedTime={lateCheckoutTime}
                  onTimeChange={handleLateCheckoutTimeChange}
                  currentCheckoutTime={formData.checkOutTime}
                  isValidating={lateCheckoutValidate.isPending}
                  validationResult={lateCheckoutValidationResult}
                  isRtl={isRtl}
                />
              ) : activeInnerTab === 'extendStay' && detail ? (
                <ExtendStayTab
                  detail={detail}
                  newCheckoutDate={newCheckoutDate}
                  onDateChange={handleExtendDateChange}
                  validationResult={extendValidationResult}
                  isValidating={extendStayValidate.isPending}
                  onSwitchToExtendWithMove={handleSwitchToExtendWithMove}
                  isRtl={isRtl}
                  propertyImage={manualReservationResponse?.result?.propertyImage}
                  checkInDate={formData.checkIn}
                  checkOutDate={formData.checkOut}
                  platformSrc={detail.platFormSRC}
                  initialGuestEmail={detail.guestEmail || manualReservationResponse?.result?.guestEmail || ''}
                  isDirectPaymentAccepted={isDirectPaymentAcceptedExtend}
                  onDirectPaymentChange={setIsDirectPaymentAcceptedExtend}
                  guestEmail={guestEmailExtend}
                  onGuestEmailChange={setGuestEmailExtend}
                  onValidationChange={setIsExtendStayValid}
                />
              ) : activeInnerTab === 'extendWithMove' ? (
                <ExtendWithMoveTab
                  extensionDate={extensionDate}
                  onDateChange={setExtensionDate}
                  availableProperties={availableProperties}
                  isLoadingProperties={propertiesLoading}
                  selectedPropertyId={selectedPropertyId}
                  onSelectProperty={setSelectedPropertyId}
                  isRtl={isRtl}
                  propertyImage={manualReservationResponse?.result?.propertyImage}
                  propertyName={manualReservationResponse?.result?.propertyName}
                  checkInDate={formData.checkIn}
                  checkOutDate={formData.checkOut}
                  platformSrc={detail?.platFormSRC}
                  initialGuestEmail={detail?.guestEmail || manualReservationResponse?.result?.guestEmail || ''}
                  isDirectPaymentAccepted={isDirectPaymentAcceptedMove}
                  onDirectPaymentChange={setIsDirectPaymentAcceptedMove}
                  guestEmail={guestEmailMove}
                  onGuestEmailChange={setGuestEmailMove}
                  onValidationChange={setIsExtendWithMoveValid}
                />
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <Typography sx={{ fontFamily: 'Tajawal, sans-serif', color: '#8E98A1', fontSize: '14px' }}>
                    {t('TaskManagemnt.comingSoon')}
                  </Typography>
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Static Footer */}
      <Box sx={{ flexShrink: 0, py: '24px' }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', alignItems: 'center', px: '30px' }}>
          
          {/* Save */}
          <Box
            onClick={!isSaveDisabled ? handleSave : undefined}
            sx={{
              width: '200px',
              py: '16px',
              backgroundColor: isSaveDisabled ? '#C4C4C4' : '#5B3FFF',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: isSaveDisabled ? 'not-allowed' : 'pointer',
              opacity: isSaveDisabled ? 0.7 : 1,
              '&:hover': !isSaveDisabled ? { backgroundColor: '#4A32E0' } : {},
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Tajawal, sans-serif',
                fontWeight: 700,
                fontSize: '14px',
                color: '#FFFFFF',
              }}
            >
              {isSaving ? t('TaskManagemnt.saving') : t('TaskManagemnt.save')}
            </Typography>
          </Box>
          {/* Cancel */}
          <Box
            onClick={onClose}
            sx={{
              width: '200px',
              py: '16px',
              border: '1px solid #5B3FFF',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              '&:hover': { backgroundColor: '#F9F8FF' },
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Tajawal, sans-serif',
                fontWeight: 700,
                fontSize: '14px',
                color: '#5B3FFF',
              }}
            >
              {t('TaskManagemnt.cancel')}
            </Typography>
          </Box>

        </Box>
      </Box>
    </Dialog>
  );
};

export default ReservationModal;
